<script setup>
import { computed, defineEmits, defineProps, ref, watch } from "vue";
import UtilDateTime from "@/utils/UtilDateTime";
import EntityService from "@/services/entityService";
import Schema from "@/api/v2/Schema.mjs";
import {
  formatarKv,
  formatarNumeroInput,
  recalcularCamposComerciaisItem
} from "../services/solicitacaoDeOrcamentoRules";

const props = defineProps({
  formularioAberto: { type: Boolean, default: false },
  modoFormulario: { type: String, default: "create" },
  formulario: { type: Object, required: true },
  statusOpcoes: { type: Array, default: () => [] },
  clientes: { type: Array, default: () => [] },
  clientesCarregando: { type: Boolean, default: false },
  clienteBusca: { type: String, default: "" },
  contatosCliente: { type: Array, default: () => [] },
  vendedores: { type: Array, default: () => [] },
  orcamentistas: { type: Array, default: () => [] },
  contatosComerciaisCliente: { type: Array, default: () => [] },
  contatosTecnicosCliente: { type: Array, default: () => [] },
  tiposDeEquipamento: { type: Array, default: () => [] },
  abrirNovoContato: { type: Boolean, default: false },
  novoContato: {
    type: Object,
    default: () => ({ nome: "", email: "", telefone: "", celular: "" })
  },
  followUps: { type: Array, default: () => [] },
  revisoes: { type: Array, default: () => [] },
  carregando: { type: Boolean, default: false },
  somenteLeitura: { type: Boolean, default: false },
  camposBloqueados: { type: Boolean, default: false },
  statusConcluido: { type: Boolean, default: false },
  hasJobAssociado: { type: Boolean, default: false },
  podeGerarJob: { type: Boolean, default: true },
  podeConcluirSolicitacao: { type: Boolean, default: false }
});

const emits = defineEmits([
  "update:formularioAberto",
  "fechar",
  "gerar-capa-proposta",
  "salvar",
  "concluir-solicitacao",
  "buscar-clientes",
  "normalizar-numericos",
  "selecionar-cliente",
  "selecionar-contato",
  "selecionar-contato-tecnico",
  "selecionar-tipo-equipamento",
  "alterar-empresa",
  "criar-contato",
  "update:abrirNovoContato",
  "novo-followup",
  "editar-followup",
  "deletar-followup",
  "gerar-revisao",
  "gerar-job",
  "add-item",
  "update-item",
  "remove-item",
  "normalizar-item"
]);

const dialogModel = computed({
  get: () => props.formularioAberto,
  set: (valor) => emits("update:formularioAberto", valor)
});

const abrirNovoContatoModel = computed({
  get: () => props.abrirNovoContato,
  set: (valor) => emits("update:abrirNovoContato", valor)
});

const bloqueiaAcoesEdicao = computed(() => {
  const textoStatus = normalizarTexto(props.formulario?.statusDescription || props.formulario?.status || "");
  return textoStatus.includes("CANCEL") || textoStatus.includes("PEDIDO COLOCADO");
});

const formularioBloqueado = computed(() => props.somenteLeitura || props.camposBloqueados);

const podeEditarCabecalho = computed(() => !props.somenteLeitura && !bloqueiaAcoesEdicao.value);

const panelCliente = ref([0]);
const panelTecnica = ref([0]);
const panelComercial = ref([0]);
const panelItens = ref([0]);
const panelObservacoes = ref([0]);
const panelFollowUp = ref([0]);
const panelRevisoes = ref([0]);

const tipoContatoDialog = ref("comercial");
const opcoesEmpresa = ["DELTA DUCON", "POWDER"];

// eslint-disable-next-line no-unused-vars
const tipoEquipamentoItemText = (item) => [
  item?.sigla || item?.codigo || item?.code,
  item?.descricao || item?.nome || item?.description || item?.name
].filter(Boolean).join(" – ");
const tipoEquipamentoFilter = (item, queryText) => {
  const texto = `${item?.sigla || item?.codigo || item?.code || ""} ${item?.descricao || item?.nome || item?.description || item?.name || ""}`.toLowerCase();
  return texto.includes((queryText || "").toLowerCase());
};

const tiposDeEquipamentoFiltrados = computed(() => {
  const lista = Array.isArray(props.tiposDeEquipamento) ? props.tiposDeEquipamento : [];
  const origem = String(props.formulario?.origem || "").toUpperCase();
  const prefixo = origem.includes("POWDER") ? "P" : "D";

  return lista.filter((item) => {
    const sigla = String(item?.sigla || item?.codigo || item?.code || "").toUpperCase();
    return sigla.startsWith(prefixo);
  });
});

const clienteItemText = (item) => {
  const codigo = item?.codigo || item?.code || item?.id || "-";
  const nome = item?.nomeFantasia || item?.razaoSocial || item?.nome || "-";
  return `${codigo} - ${nome}`;
};

// Items helpers
const hasItens = computed(() => Array.isArray(props.formulario?.itens) && props.formulario.itens.length > 0);

const camposComercialReadonly = computed(() => true);

function parseNumeroLocal(valor) {
  if (typeof valor === "number") return Number.isFinite(valor) ? valor : 0;
  if (!valor) return 0;
  const texto = String(valor).trim().replace(/\s/g, "").replace(/(\d)\.(?=\d{3}(\D|$))/g, "$1").replace(",", ".");
  const numero = Number(texto);
  return Number.isFinite(numero) ? numero : 0;
}

function calcularValorTotalItem(item) {
  const quantidade = parseNumeroLocal(item.quantidade);
  const preco = parseNumeroLocal(item.precoVenda_sIPI_cICMS);
  return quantidade * preco;
}

function calcularValorDisponivelTotalItem(item) {
  const quantidade = parseNumeroLocal(item.quantidade);
  const valorDisponivel = parseNumeroLocal(item.valorDisponivel);
  return quantidade * valorDisponivel;
}

function formatarMoeda(valor) {
  return Number(parseNumeroLocal(valor)).toLocaleString("pt-BR", {
    style: "currency",
    currency: "BRL",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

function formatarValorTotal(item) {
  const val = calcularValorTotalItem(item);
  return formatarMoeda(val);
}

function formatarValorDisponivelTotal(item) {
  const val = parseNumeroLocal(item?.valorDisponivelTotal);
  if (val > 0) return formatarMoeda(val);
  return formatarMoeda(calcularValorDisponivelTotalItem(item || {}));
}

function formatarValorNumero(valor) {
  return formatarNumeroInput(parseNumeroLocal(valor));
}

function formatarValorDisponivelTotalNumero(item) {
  const val = parseNumeroLocal(item?.valorDisponivelTotal);
  if (val > 0) return formatarValorNumero(val);
  return formatarValorNumero(calcularValorDisponivelTotalItem(item || {}));
}

function formatarKiNumero(valor) {
  return formatarNumeroInput(parseNumeroLocal(valor), 3);
}

function formatarKvNumero(valor) {
  return formatarKv(valor ?? 0);
}

function formatarValorTotalNumero(item) {
  const valorTotal = item?.vlrTotal || item?.valorTotal || calcularValorTotalItem(item);
  return formatarValorNumero(valorTotal);
}

function formatarMoedaDigitacao(valor) {
  const apenasDigitos = String(valor || "").replace(/\D/g, "");
  const centavos = Number(apenasDigitos || 0);
  return Number(centavos / 100).toLocaleString("pt-BR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2
  });
}

function aoDigitarMoedaNovoItem(campo, valor) {
  novoItemForm.value[campo] = formatarMoedaDigitacao(valor);
  recalcularNovoItemForm();
}

function obterIndexItem(item) {
  return (props.formulario?.itens || []).indexOf(item);
}

const dialogFollowUp = ref(false);
const followUpForm = ref({ description: "", data: "", contato_comercial: "", status: null });
const followUpErros = ref({ description: "", status: "", contato_comercial: "" });
const dialogConfirmarFechamentoCancelado = ref(false);
const dialogConfirmarConclusao = ref(false);
const dialogNovoItem = ref(false);
const indiceItemEmEdicao = ref(null);
const itensCatalogo = ref([]);
const itensCatalogoCarregando = ref(false);
const novoItemForm = ref({
  itemId: "",
  descricao: "",
  quantidade: 1,
  valorDisponivel: "0,00",
  valorDisponivelTotal: "0,00",
  porcentagemPIS: "0,00",
  porcentagemCOFINS: "0,00",
  ki: "0,000",
  kidoCusto: "0,00",
  porcentagemISS: "0,00",
  porcentagemICMSVenda: "0,00",
  pocentagemIPIVenda: "0,00",
  kv: "0,000",
  precoVenda_sIPI_cICMS: "0,00",
  precoVenda_cIPI_cICMS: "0,00",
  precoVenda_sIPI_sICMS: "0,00",
  precoVenda_cISS: "0,00",
  valorICMS: "0,00",
  valorIPI: "0,00",
  vlrTotal: "0,00",
  observacao: ""
});

const novoItemErros = ref({
  itemId: "",
  descricao: "",
  quantidade: ""
});

const novoItemCalculado = computed(() => recalcularCamposComerciaisItem(novoItemForm.value));

const novoItemReadonly = computed(() => ({
  kidoCusto: formatarNumeroInput(novoItemCalculado.value.kidoCusto),
  precoVenda_cIPI_cICMS: formatarNumeroInput(novoItemCalculado.value.precoVenda_cIPI_cICMS),
  precoVenda_sIPI_sICMS: formatarNumeroInput(novoItemCalculado.value.precoVenda_sIPI_sICMS),
  precoVenda_cISS: formatarNumeroInput(novoItemCalculado.value.precoVenda_cISS),
  kv: formatarNumeroInput(novoItemCalculado.value.kv, 3),
  valorICMS: formatarNumeroInput(novoItemCalculado.value.valorICMS),
  valorIPI: formatarNumeroInput(novoItemCalculado.value.valorIPI),
  valorDisponivelTotal: formatarNumeroInput(novoItemCalculado.value.valorDisponivelTotal),
  vlrTotal: formatarNumeroInput(novoItemCalculado.value.vlrTotal)
}));

const modoVisualizacaoItem = ref(false);

const itemSomenteLeitura = computed(() => modoVisualizacaoItem.value);

const tituloDialogItem = computed(() => (
  modoVisualizacaoItem.value
    ? "Visualizar Item da Solicitação"
    : (indiceItemEmEdicao.value !== null ? "Editar Item da Solicitação" : "Novo Item da Solicitação")
));

function criarNovoItemVazio() {
  return {
    itemId: "",
    descricao: "",
    quantidade: 1,
    valorDisponivel: "0,00",
    valorDisponivelTotal: "0,00",
    porcentagemPIS: "0,00",
    porcentagemCOFINS: "0,00",
    ki: "0,000",
    kidoCusto: "0,00",
    porcentagemISS: "0,00",
    porcentagemICMSVenda: "0,00",
    pocentagemIPIVenda: "0,00",
    kv: "0,000",
    precoVenda_sIPI_cICMS: "0,00",
    precoVenda_cIPI_cICMS: "0,00",
    precoVenda_sIPI_sICMS: "0,00",
    precoVenda_cISS: "0,00",
    valorICMS: "0,00",
    valorIPI: "0,00",
    vlrTotal: "0,00",
    observacao: ""
  };
}

function extrairListaResponse(response = {}) {
  if (Array.isArray(response?.data?.data?.data)) return response.data.data.data;
  if (Array.isArray(response?.data?.data)) return response.data.data;
  if (Array.isArray(response?.data)) return response.data;
  return [];
}

function normalizarRegistroApi(registro = {}) {
  const currentData = registro?._currentData || registro?.currentData || {};
  const temDadosCurrentData = currentData && typeof currentData === "object" && Object.keys(currentData).length > 0;
  const dados = temDadosCurrentData ? currentData : registro;

  return {
    ...dados,
    id: dados?.id || dados?._id || registro?.id || registro?._id || "",
    _id: dados?._id || dados?.id || registro?._id || registro?.id || ""
  };
}

function normalizarTextoBusca(valor = "") {
  return String(valor || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
}

function filtroItemCatalogo(item = {}, queryText = "") {
  if (!queryText) return true;
  const busca = normalizarTextoBusca(queryText);
  const alvo = normalizarTextoBusca([
    item?.code,
    item?.description,
    item?.display
  ].filter(Boolean).join(" "));
  return alvo.includes(busca);
}

function normalizarOpcaoItem(item = {}) {
  const normalizado = normalizarRegistroApi(item);
  const id = normalizado?.id || normalizado?._id || "";
  const codigo = normalizado?.codeExt || normalizado?.code || normalizado?.codigo || "";
  const descricao = normalizado?.description || normalizado?.descricao || normalizado?.name || normalizado?.nome || "";
  return {
    ...normalizado,
    id,
    valueId: id,
    code: codigo,
    description: descricao,
    display: [codigo, descricao].filter(Boolean).join(" - ") || descricao || codigo || "Sem descrição"
  };
}

async function carregarItensCatalogo() {
  if (itensCatalogoCarregando.value) return;
  if (itensCatalogo.value.length > 0) return;

  itensCatalogoCarregando.value = true;
  try {
    const response = await new EntityService(Schema.ITEM).getAll(1, 20000, "", "", null, null, null, "codeExt");
    itensCatalogo.value = extrairListaResponse(response).map((item) => normalizarOpcaoItem(item));
  } catch (_) {
    itensCatalogo.value = [];
  } finally {
    itensCatalogoCarregando.value = false;
  }
}

function aoAlterarItemSelecionado(itemId) {
  const itemSelecionado = itensCatalogo.value.find((item) => item.valueId === itemId || item.id === itemId);
  if (!itemSelecionado) return;

  novoItemForm.value.itemId = itemSelecionado.valueId;
  novoItemForm.value.descricao = itemSelecionado.description || "";
  novoItemForm.value.itemCodigo = itemSelecionado.code || "";
  novoItemForm.value.itemCodigoExt = itemSelecionado.code || "";
  recalcularNovoItemForm();
}

function montarDescricaoItemGrid(item = {}) {
  const codigo = String(item?.itemCodigoExt || item?.itemCodigo || item?.codeExt || item?.code || item?.codigo || "").trim();
  const descricao = String(item?.descricao || item?.description || "").trim();
  if (codigo && descricao) return `${codigo} - ${descricao}`;
  return descricao || codigo || "-";
}

function recalcularNovoItemForm() {}

watch(
  () => [
    novoItemForm.value.quantidade,
    novoItemForm.value.valorDisponivel,
    novoItemForm.value.ki,
    novoItemForm.value.porcentagemISS,
    novoItemForm.value.porcentagemICMSVenda,
    novoItemForm.value.pocentagemIPIVenda,
    novoItemForm.value.precoVenda_sIPI_cICMS
  ],
  () => {
    if (!dialogNovoItem.value) return;
    recalcularNovoItemForm();
  }
);

function normalizarTexto(valor = "") {
  return String(valor || "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toUpperCase();
}

const statusOpcoesFollowUp = computed(() => {
  return (Array.isArray(props.statusOpcoes) ? props.statusOpcoes : []).filter((item) => {
    const descricao = normalizarTexto(item?.description || item?.descricao || item?.name);
    return descricao !== "PEDIDO COLOCADO" && descricao !== "CONCLUIDO";
  });
});

const statusConcluidoFollowUp = computed(() => {
  return (Array.isArray(props.statusOpcoes) ? props.statusOpcoes : []).find((item) => {
    const descricao = normalizarTexto(item?.description || item?.descricao || item?.name);
    return descricao === "CONCLUIDO";
  }) || null;
});

const descricaoStatusConcluidoFollowUp = computed(() => {
  return statusConcluidoFollowUp.value?.description || "CONCLUÍDO";
});

function formatarData(valor) {
  if (!valor) return "-";
  const dataBrasil = UtilDateTime.toFormatBrazil({ dateTime: valor });
  return dataBrasil || String(valor).trim() || "-";
}

const statusFormularioCancelado = computed(() => {
  const textoStatus = normalizarTexto(
    props.formulario?.statusDescription || props.formulario?.status || ""
  );
  return textoStatus.includes("CANCEL");
});

const deveConfirmarFechamentoCancelado = computed(() => {
  return props.modoFormulario === "edit" && statusFormularioCancelado.value;
});

const statusFormularioConcluido = computed(() => {
  if (props.statusConcluido) return true;
  const textoStatus = normalizarTexto(
    props.formulario?.statusDescription || props.formulario?.status || ""
  );
  return textoStatus === "CONCLUIDO";
});

const statusFormularioPedidoColocado = computed(() => {
  const textoStatus = normalizarTexto(
    props.formulario?.statusDescription || props.formulario?.status || ""
  );
  return textoStatus.includes("PEDIDO COLOCADO");
});

const podeIncluirFollowUp = computed(() => {
  if (!props.formulario?.id) return false;
  if (props.somenteLeitura) return false;
  return !bloqueiaAcoesEdicao.value;
});

const podeEditarItens = computed(() => {
  if (formularioBloqueado.value) return false;

  const textoStatus = normalizarTexto(
    props.formulario?.statusDescription || props.formulario?.status || ""
  );

  if (!textoStatus) return true;
  if (textoStatus.includes("PEDIDO COLOCADO")) return false;
  if (textoStatus.includes("CONCLUIDO")) return true;

  return (
    textoStatus.includes("EM EXECUCAO") ||
    textoStatus.includes("NOVO") ||
    textoStatus.includes("REVISAO GERADA") ||
    textoStatus.includes("REVISAO")
  );
});

const podeExcluirItens = computed(() => {
  if (!podeEditarItens.value) return false;
  return !statusFormularioConcluido.value && !statusFormularioPedidoColocado.value;
});

function aoClicarFechar() {
  if (deveConfirmarFechamentoCancelado.value) {
    dialogConfirmarFechamentoCancelado.value = true;
    return;
  }

  emits("fechar");
}

function confirmarFechamentoCancelado() {
  dialogConfirmarFechamentoCancelado.value = false;
  emits("fechar");
}

function cancelarFechamentoCancelado() {
  dialogConfirmarFechamentoCancelado.value = false;
}

function abrirConfirmacaoConclusao() {
  dialogConfirmarConclusao.value = true;
}

function aoClicarConcluirSolicitacao() {
  if (!props.podeConcluirSolicitacao) {
    emits("concluir-solicitacao");
    return;
  }

  abrirConfirmacaoConclusao();
}

function cancelarConfirmacaoConclusao() {
  dialogConfirmarConclusao.value = false;
}

function confirmarConclusaoSolicitacao() {
  dialogConfirmarConclusao.value = false;
  emits("concluir-solicitacao");
}

function abrirNovoFollowUp() {
  followUpForm.value = {
    description: "",
    data: UtilDateTime.todayISO(),
    contato_comercial: "",
    status: statusFormularioConcluido.value ? (statusConcluidoFollowUp.value?.id || null) : null
  };
  followUpErros.value = { description: "", status: "", contato_comercial: "" };
  dialogFollowUp.value = true;
}

function confirmarFollowUp() {
  const descricao = String(followUpForm.value.description || "").trim();
  const status = statusFormularioConcluido.value
    ? (statusConcluidoFollowUp.value?.id || followUpForm.value.status)
    : followUpForm.value.status;
  const contatoComercial = String(followUpForm.value.contato_comercial || "").trim();

  if (statusFormularioConcluido.value && !statusConcluidoFollowUp.value?.id) {
    followUpErros.value = {
      description: descricao ? "" : "Descrição é obrigatória",
      status: "Status CONCLUÍDO não encontrado nas opções",
      contato_comercial: ""
    };
    return;
  }

  const statusSelecionado = statusOpcoesFollowUp.value.find((item) => item?.id === status);
  const descricaoStatus = normalizarTexto(
    statusSelecionado?.description ||
    statusSelecionado?.descricao ||
    statusSelecionado?.name ||
    descricaoStatusConcluidoFollowUp.value
  );
  const contatoObrigatorio = descricaoStatus === "CONCLUIDO";

  followUpErros.value = {
    description: descricao ? "" : "Descrição é obrigatória",
    status: status ? "" : "Status é obrigatório",
    contato_comercial: (contatoObrigatorio && !contatoComercial)
      ? "Contato comercial é obrigatório para status CONCLUÍDO"
      : ""
  };

  if (!descricao || !status || (contatoObrigatorio && !contatoComercial)) return;

  followUpForm.value.description = descricao;
  followUpForm.value.contato_comercial = contatoComercial;
  emits("novo-followup", { ...followUpForm.value });
  dialogFollowUp.value = false;
}

const headersFollowUp = [
  { text: "Codigo", value: "code", sortable: false },
  { text: "Descricao", value: "description", sortable: false },
  { text: "Data", value: "data", sortable: false },
  { text: "Contato Comercial", value: "contato_comercial", sortable: false },
  { text: "Status", value: "statusDescription", sortable: false }
];

const headersRevisoes = [
  { text: "Revisao", value: "revisao", sortable: false },
  { text: "Numero Proposta", value: "numeroProposta", sortable: false },
  { text: "Data Geracao", value: "dateGenerate", sortable: false },
  { text: "Status", value: "statusDescription", sortable: false },
  { text: "Tipo de Equipamento", value: "_mirror_tipoDeEquipamento", sortable: false }
];

const headersItens = [
  { text: "Ações", value: "acoes", sortable: false, width: "90px" },
  { text: "Descrição", value: "descricao", sortable: false },
  { text: "Qtd", value: "quantidade", sortable: false, width: "80px" },
  { text: "Custo Unit.", value: "valorDisponivel", sortable: false, width: "120px" },
  { text: "Custo Total", value: "valorDisponivelTotal", sortable: false, width: "120px" },
  { text: "%PIS", value: "porcentagemPIS", sortable: false, width: "85px" },
  { text: "%COFINS", value: "porcentagemCOFINS", sortable: false, width: "95px" },
  { text: "KI", value: "ki", sortable: false, width: "90px" },
  { text: "%ISS Venda", value: "porcentagemISS", sortable: false, width: "100px" },
  { text: "%ICMS Venda", value: "porcentagemICMSVenda", sortable: false, width: "115px" },
  { text: "%IPI Venda", value: "pocentagemIPIVenda", sortable: false, width: "105px" },
  { text: "KV", value: "kv", sortable: false, width: "90px" },
  { text: "Preço de Venda s/IPI", value: "precoVenda_sIPI_cICMS", sortable: false, width: "150px" },
  { text: "Valor Total s/IPI", value: "valorTotal", sortable: false, width: "135px" }
];

const historicoDialog = ref(false);
const historicoLoading = ref(false);
const historicoItems = ref([]);
const historicoItemSelected = ref(null);

function openHistorico(item, itemIndex) {
  historicoItemSelected.value = { ...item };
  historicoDialog.value = true;
  historicoLoading.value = true;
  historicoItems.value = [];

  // TODO: Implementar carregamento de histórico do item
  // Padrão: utilizar entityServiceHistorico.getAll(1, 1000, item.id, ['solicitacaoDeOrcamentoItem'], ...)
  // Por enquanto retorna um mock ou vazio para estrutura estar pronta
  
  // Simulação: em produção, fazer chamada ao backend
  setTimeout(() => {
    historicoLoading.value = false;
    // historicoItems.value será populado pela chamada ao backend
  }, 500);
}

async function abrirNovoItem() {
  if (!podeEditarItens.value) return;
  if (dialogNovoItem.value) return;

  modoVisualizacaoItem.value = false;
  indiceItemEmEdicao.value = null;
  novoItemForm.value = criarNovoItemVazio();
  novoItemErros.value = { itemId: "", descricao: "", quantidade: "" };

  carregarItensCatalogo();
  recalcularNovoItemForm();
  dialogNovoItem.value = true;
}

async function abrirEditarItem(item = {}, index = -1) {
  if (!podeEditarItens.value) return;
  if (!Number.isInteger(index) || index < 0) return;

  modoVisualizacaoItem.value = false;
  indiceItemEmEdicao.value = index;
  novoItemForm.value = {
    ...criarNovoItemVazio(),
    ...item,
    itemId: item?.itemId || item?.item || "",
    descricao: item?.descricao || item?.description || ""
  };
  novoItemErros.value = { itemId: "", descricao: "", quantidade: "" };

  carregarItensCatalogo();
  recalcularNovoItemForm();
  dialogNovoItem.value = true;
}

async function abrirVisualizarItem(item = {}, index = -1) {
  if (!Number.isInteger(index) || index < 0) return;

  modoVisualizacaoItem.value = true;
  indiceItemEmEdicao.value = index;
  novoItemForm.value = {
    ...criarNovoItemVazio(),
    ...item,
    itemId: item?.itemId || item?.item || "",
    descricao: item?.descricao || item?.description || ""
  };
  novoItemErros.value = { itemId: "", descricao: "", quantidade: "" };

  carregarItensCatalogo();
  recalcularNovoItemForm();
  dialogNovoItem.value = true;
}

watch(
  () => props.formularioAberto,
  (aberto) => {
    if (!aberto) return;
    carregarItensCatalogo();
  }
);

function confirmarNovoItem() {
  const itemId = String(novoItemForm.value.itemId || "").trim();
  const descricao = String(novoItemForm.value.descricao || "").trim();
  const quantidade = parseNumeroLocal(novoItemForm.value.quantidade);

  novoItemErros.value = {
    itemId: "",
    descricao: descricao ? "" : "Descrição é obrigatória",
    quantidade: quantidade > 0 ? "" : "Quantidade deve ser maior que zero"
  };
  if (!descricao || quantidade <= 0) return false;

  const calculado = novoItemCalculado.value;

  try {
    const payloadItem = {
      itemId,
      itemCodigo: novoItemForm.value.itemCodigo || "",
      itemCodigoExt: novoItemForm.value.itemCodigoExt || "",
      descricao,
      description: descricao,
      quantidade,
      qty: quantidade,
      valorDisponivel: novoItemForm.value.valorDisponivel,
      valorDisponivelTotal: calculado.valorDisponivelTotal,
      porcentagemPIS: novoItemForm.value.porcentagemPIS,
      porcentagemCOFINS: novoItemForm.value.porcentagemCOFINS,
      ki: novoItemForm.value.ki,
      kidoCusto: novoItemForm.value.kidoCusto,
      porcentagemISS: novoItemForm.value.porcentagemISS,
      porcentagemIIS: novoItemForm.value.porcentagemISS,
      porcentagemICMSVenda: novoItemForm.value.porcentagemICMSVenda,
      pocentagemIPIVenda: novoItemForm.value.pocentagemIPIVenda,
      porcentagemIPI: novoItemForm.value.pocentagemIPIVenda,
      kv: calculado.kv,
      precoVenda_sIPI_cICMS: novoItemForm.value.precoVenda_sIPI_cICMS,
      precoVenda_cIPI_cICMS: calculado.precoVenda_cIPI_cICMS,
      precoVenda_sIPI_sICMS: calculado.precoVenda_sIPI_sICMS,
      precoVenda_cISS: calculado.precoVenda_cISS,
      valorICMS: calculado.valorICMS,
      valorIPI: calculado.valorIPI,
      vlrTotal: calculado.vlrTotal,
      valorTotal: calculado.vlrTotal,
      observacao: novoItemForm.value.observacao
    };

    if (indiceItemEmEdicao.value !== null) {
      emits("update-item", { index: indiceItemEmEdicao.value, item: payloadItem });
    } else {
      emits("add-item", payloadItem);
    }
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error("Erro ao adicionar item da solicitação:", error);
    return false;
  }

  return true;
}

function cancelarNovoItem() {
  modoVisualizacaoItem.value = false;
  indiceItemEmEdicao.value = null;
  dialogNovoItem.value = false;
}

function adicionarNovoItem() {
  if (modoVisualizacaoItem.value) {
    cancelarNovoItem();
    return;
  }

  const sucesso = confirmarNovoItem();
  if (!sucesso) return;
  indiceItemEmEdicao.value = null;
  dialogNovoItem.value = false;
}
</script>

<template>
  <div>
    <v-dialog v-model="dialogModel" fullscreen persistent transition="dialog-bottom-transition">
      <v-card tile class="so-modal">
        <div class="so-topbar">
          <div class="so-topbar__left-actions">
            <v-btn class="so-topbar__btn so-topbar__btn--close" color="error" dark :disabled="props.carregando" @click="aoClicarFechar">
              Fechar
            </v-btn>
            <v-btn
              class="so-topbar__btn so-topbar__btn--proposal"
              color="primary"
              dark
              :disabled="props.carregando"
              @click="emits('gerar-capa-proposta')"
            >
              Gerar Capa Proposta
            </v-btn>
          </div>

          <p class="so-topbar__title">
            <v-icon color="white" class="mr-2">mdi-file-document-edit-outline</v-icon>
            Solicitação de Orçamento
          </p>

          <div class="so-topbar__actions">
            <v-btn
              v-if="podeEditarCabecalho && !statusFormularioConcluido"
              class="so-topbar__btn"
              color="primary"
              dark
              :loading="props.carregando"
              @click="emits('salvar')"
            >
              Gravar
            </v-btn>
            <v-btn
              v-if="podeEditarCabecalho && statusFormularioConcluido && !props.hasJobAssociado"
              class="so-topbar__btn"
              color="warning"
              dark
              :disabled="props.carregando || !props.formulario?.id"
              @click="emits('gerar-revisao')"
            >
              Gerar Revisão
            </v-btn>
            <v-btn
              v-if="podeEditarCabecalho && props.podeGerarJob"
              class="so-topbar__btn"
              color="success"
              dark
              :disabled="props.carregando || !props.formulario?.id"
              @click="emits('gerar-job')"
            >
                Gerar Job
            </v-btn>
            <v-btn
              v-if="podeEditarCabecalho && !statusFormularioConcluido"
              class="so-topbar__btn so-topbar__btn--auto-width"
              color="success"
              dark
              :disabled="props.carregando || !props.formulario?.id"
              @click="aoClicarConcluirSolicitacao()"
            >
              Concluir Solicitação
            </v-btn>
          </div>
        </div>

        <div class="so-body">
          <div :class="['so-form', { 'so-form--readonly': formularioBloqueado }]">
            <fieldset class="so-fieldset">

            <!-- Cabecalho -->
            <v-row dense align="center" class="mb-0">
              <v-col cols="12" md="4">
                <v-text-field
                  label="Numero Proposta"
                  v-model="props.formulario.numeroProposta"
                  readonly outlined dense hide-details class="so-readonly"
                />
              </v-col>
              <v-col cols="12" md="8">
                <v-autocomplete
                  :items="tiposDeEquipamentoFiltrados"
                  :item-text="tipoEquipamentoItemText"
                  :filter="tipoEquipamentoFilter"
                  item-value="id"
                  label="Tipo de Equipamento"
                  v-model="props.formulario.tipoDeEquipamento"
                  outlined dense hide-details clearable
                  @change="(v) => emits('selecionar-tipo-equipamento', v)"
                >
                  <template v-slot:item="{ item }">
                    <span><strong>{{ item.sigla || item.codigo || item.code }}</strong> – {{ item.descricao || item.nome || item.description || item.name }}</span>
                  </template>
                  <template v-slot:selection="{ item }">
                    <span><strong>{{ item.sigla || item.codigo || item.code }}</strong> – {{ item.descricao || item.nome || item.description || item.name }}</span>
                  </template>
                </v-autocomplete>
              </v-col>
            </v-row>

            <v-row dense class="mt-3">
              <v-col cols="12" md="3">
                <v-select
                  v-if="!formularioBloqueado"
                  :items="opcoesEmpresa"
                  label="Empresa"
                  v-model="props.formulario.origem"
                  outlined dense hide-details
                  @change="(v) => emits('alterar-empresa', v)"
                />
                <v-text-field
                  v-else
                  label="Empresa"
                  v-model="props.formulario.origem"
                  readonly outlined dense hide-details class="so-readonly"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  label="Status"
                  :value="props.formulario.statusDescription || props.formulario.status || ''"
                  readonly outlined dense hide-details class="so-readonly"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  label="Job"
                  v-model="props.formulario.jobNome"
                  readonly outlined dense hide-details class="so-readonly"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  label="Revisao"
                  :value="props.formulario.ultimaRevisao || props.formulario.revisao || 'ORIGINAL'"
                  readonly outlined dense hide-details class="so-readonly"
                />
              </v-col>
              <v-col cols="12" md="3">
                <v-text-field
                  label="Data Geracao"
                  type="date"
                  v-model="props.formulario.dateGenerate"
                  readonly outlined dense hide-details class="so-readonly"
                />
              </v-col>
            </v-row>

            <!-- SECAO: Cliente -->
            <v-expansion-panels v-model="panelCliente" flat multiple class="mt-4 so-panel">
              <v-expansion-panel>
                <v-expansion-panel-header class="so-panel__header">
                  <span class="subtitle-2 font-weight-medium">Cliente</span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row dense>
                    <v-col cols="12" md="6">
                      <v-autocomplete
                        :items="props.clientes"
                        :item-text="clienteItemText"
                        item-value="id"
                        :search-input="props.clienteBusca"
                        :loading="props.clientesCarregando"
                        no-filter
                        label="Cliente"
                        v-model="props.formulario.cliente"
                        outlined dense hide-details clearable
                        @focus="() => emits('buscar-clientes', props.clienteBusca || '')"
                        @update:search-input="(v) => emits('buscar-clientes', v)"
                        @change="(v) => emits('selecionar-cliente', v)"
                      />
                    </v-col>
                    <v-col cols="12" md="6">
                      <v-text-field
                        label="Referencia do Cliente"
                        v-model="props.formulario.cliente_referencia"
                        outlined dense hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <v-text-field
                        label="CNPJ"
                        :value="props.formulario.clienteCnpj"
                        readonly outlined dense hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3" />
                    <v-col cols="12" md="6" class="mt-3">
                      <v-autocomplete
                        :items="props.vendedores"
                        item-text="nome"
                        item-value="id"
                        label="Vendedor"
                        v-model="props.formulario.vendedor"
                        outlined dense hide-details clearable
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <v-autocomplete
                        :items="props.orcamentistas"
                        item-text="nome"
                        item-value="id"
                        label="Orcamentista"
                        v-model="props.formulario.orcamentista"
                        outlined dense hide-details clearable
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <v-autocomplete
                        :items="props.contatosComerciaisCliente"
                        item-text="nome"
                        item-value="id"
                        label="Contato Comercial"
                        v-model="props.formulario.contato"
                        outlined dense hide-details clearable
                        :disabled="!props.formulario.cliente"
                        @change="(v) => emits('selecionar-contato', v)"
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <v-text-field
                        label="Contato Comercial Telefone"
                        v-model="props.formulario.contatoComercialTelefone"
                        readonly outlined dense hide-details class="so-readonly"
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <v-text-field
                        label="Contato Comercial Celular"
                        v-model="props.formulario.contatoComercialCelular"
                        readonly outlined dense hide-details class="so-readonly"
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <v-text-field
                        label="Contato Comercial E-mail"
                        v-model="props.formulario.contatoComercialEmail"
                        readonly outlined dense hide-details class="so-readonly"
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <v-autocomplete
                        :items="props.contatosTecnicosCliente"
                        item-text="nome"
                        item-value="id"
                        label="Contato Tecnico"
                        v-model="props.formulario.contatoTecnico"
                        outlined dense hide-details clearable
                        :disabled="!props.formulario.cliente"
                        @change="(v) => emits('selecionar-contato-tecnico', v)"
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <v-text-field
                        label="Contato Tecnico Nome"
                        v-model="props.formulario.contatoTecnicoNome"
                        readonly outlined dense hide-details class="so-readonly"
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <v-text-field
                        label="Contato Tecnico Telefone"
                        v-model="props.formulario.contatoTecnicoTelefone"
                        readonly outlined dense hide-details class="so-readonly"
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <v-text-field
                        label="Contato Tecnico Celular"
                        v-model="props.formulario.contatoTecnicoCelular"
                        readonly outlined dense hide-details class="so-readonly"
                      />
                    </v-col>
                    <v-col cols="12" md="6" class="mt-3">
                      <v-text-field
                        label="Contato Tecnico E-mail"
                        v-model="props.formulario.contatoTecnicoEmail"
                        readonly outlined dense hide-details class="so-readonly"
                      />
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- SECAO: Proposta Tecnica -->
            <v-expansion-panels v-model="panelTecnica" flat multiple class="mt-2 so-panel">
              <v-expansion-panel>
                <v-expansion-panel-header class="so-panel__header">
                  <span class="subtitle-2 font-weight-medium">Abertura de solicitacao de Orcamento Tecnica</span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row dense class="mt-1">
                    <v-col cols="12">
                      <v-textarea
                        label="Descrição do Projeto"
                        v-model="props.formulario.projetoDescricao"
                        outlined dense hide-details
                        rows="3"
                        auto-grow
                        :readonly="formularioBloqueado"
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="Data Solicitacao"
                        type="date"
                        v-model="props.formulario.dataSolicitacao"
                        outlined dense hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="Data Desejada"
                        type="date"
                        v-model="props.formulario.dataDesejada"
                        outlined dense hide-details
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="Data Conclusao"
                        type="date"
                        v-model="props.formulario.dataConclusao"
                        outlined dense hide-details
                      />
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- SECAO: Proposta Comercial -->
            <v-expansion-panels v-model="panelComercial" flat multiple class="mt-2 so-panel">
              <v-expansion-panel>
                <v-expansion-panel-header class="so-panel__header">
                  <span class="subtitle-2 font-weight-medium">Abertura de solicitacao de Orcamento Comercial</span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row dense class="mt-1">
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="Custo Disp. s/ICMS, PIS, COFINS c/IPI"
                        v-model="props.formulario.valorDisponivel"
                        readonly
                        class="so-readonly"
                        outlined dense hide-details
                        :hint="hasItens ? 'Soma dos itens' : ''"
                        :persistent-hint="hasItens"
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="Preco de Venda c/ICMS s/IPI"
                        v-model="props.formulario.precoVenda_sIPI_cICMS"
                        readonly
                        class="so-readonly"
                        outlined dense hide-details
                        :hint="hasItens ? 'Soma dos itens' : ''"
                        :persistent-hint="hasItens"
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="Valor do ICMS"
                        v-model="props.formulario.valorICMS"
                        readonly
                        class="so-readonly"
                        outlined dense hide-details
                        :hint="hasItens ? 'Soma dos itens' : ''"
                        :persistent-hint="hasItens"
                      />
                    </v-col>
                    <v-col cols="12" md="3">
                      <v-text-field
                        label="Valor do IPI"
                        v-model="props.formulario.valorIPI"
                        readonly
                        class="so-readonly"
                        outlined dense hide-details
                        :hint="hasItens ? 'Soma dos itens' : ''"
                        :persistent-hint="hasItens"
                      />
                    </v-col>
                    <v-col cols="12" md="3" class="mt-3">
                      <v-text-field
                        label="Preco de Venda c/ICMS c/IPI"
                        v-model="props.formulario.precoVenda_cIPI_cICMS"
                        readonly
                        class="so-readonly"
                        outlined dense hide-details
                        :hint="hasItens ? 'Soma dos itens' : ''"
                        :persistent-hint="hasItens"
                      />
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- SECAO: Itens da Solicitacao -->
            <v-expansion-panels v-model="panelItens" flat multiple class="mt-2 so-panel">
              <v-expansion-panel>
                <v-expansion-panel-header class="so-panel__header">
                  <span class="subtitle-2 font-weight-medium">
                    Itens da Solicitação
                    <v-chip v-if="hasItens" x-small color="primary" text-color="white" class="ml-2">
                      {{ (props.formulario.itens || []).length }}
                    </v-chip>
                  </span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div class="d-flex align-center mb-2" style="gap:8px">
                    <v-btn
                      v-if="podeEditarItens"
                      icon small color="primary"
                      @click="abrirNovoItem()"
                    >
                      <v-icon>mdi-plus</v-icon>
                    </v-btn>
                    <span v-if="hasItens" class="caption grey--text">
                      Os valores de abertura são calculados automaticamente a partir dos itens
                    </span>
                  </div>
                  <v-data-table
                    :headers="headersItens"
                    :items="props.formulario.itens || []"
                    dense hide-default-footer hide-default-header class="elevation-0"
                    no-data-text="Nenhum item adicionado"
                  >
                    <template v-slot:header>
                      <thead class="v-data-table-header">
                        <tr>
                          <th class="text-left" style="width:90px;">Ações</th>
                          <th class="text-left" style="min-width:240px;">Descrição</th>
                          <th class="text-left" style="width:80px;">Qtd</th>
                          <th class="text-left" style="width:120px;">Custo Unit.</th>
                          <th class="text-left" style="width:120px;">Custo Total</th>
                          <th class="text-left" style="width:85px;">%PIS</th>
                          <th class="text-left" style="width:95px;">%COFINS</th>
                          <th class="text-left" style="width:90px;">KI</th>
                          <th class="text-left" style="width:100px;">%ISS Venda</th>
                          <th class="text-left" style="width:115px;">%ICMS Venda</th>
                          <th class="text-left" style="width:105px;">%IPI Venda</th>
                          <th class="text-left" style="width:90px;">KV</th>
                          <th class="text-left" style="width:150px;">Preço de Venda s/IPI</th>
                          <th class="text-left" style="width:135px;">Valor Total s/IPI</th>
                        </tr>
                      </thead>
                    </template>
                    <template v-slot:item="{ item, index }">
                      <tr>
                        <td>
                          <div v-if="podeEditarItens" class="d-flex align-center" style="gap:4px;">
                            <v-btn
                              icon x-small color="primary"
                              @click="abrirEditarItem(item, index)"
                            >
                              <v-icon small>mdi-pencil</v-icon>
                            </v-btn>
                            <v-btn
                              v-if="podeExcluirItens"
                              icon x-small color="error"
                              @click="emits('remove-item', index)"
                            >
                              <v-icon small>mdi-delete</v-icon>
                            </v-btn>
                          </div>
                          <div v-else class="d-flex align-center" style="gap:4px;">
                            <v-btn
                              icon x-small color="info"
                              @click="abrirVisualizarItem(item, index)"
                            >
                              <v-icon small>mdi-eye</v-icon>
                            </v-btn>
                          </div>
                        </td>
                        <td class="so-item-descricao">{{ montarDescricaoItemGrid(item) }}</td>
                        <td>{{ item.quantidade }}</td>
                        <td>{{ formatarValorNumero(item.valorDisponivel) }}</td>
                        <td>{{ formatarValorDisponivelTotalNumero(item) }}</td>
                        <td>{{ item.porcentagemPIS || '0,00' }}</td>
                        <td>{{ item.porcentagemCOFINS || '0,00' }}</td>
                        <td>{{ formatarKiNumero(item.ki) }}</td>
                        <td>{{ item.porcentagemISS || '0,00' }}</td>
                        <td>{{ item.porcentagemICMSVenda || '0,00' }}</td>
                        <td>{{ item.pocentagemIPIVenda || '0,00' }}</td>
                        <td>{{ formatarKvNumero(item.kv) }}</td>
                        <td>{{ formatarValorNumero(item.precoVenda_sIPI_cICMS) }}</td>
                        <td class="font-weight-medium">{{ formatarValorTotalNumero(item) }}</td>
                      </tr>
                    </template>
                    <template v-slot:no-data>
                      <div class="pa-3 text-center grey--text">Nenhum item adicionado</div>
                    </template>
                  </v-data-table>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- SECAO: Observacoes -->
            <v-expansion-panels v-model="panelObservacoes" flat multiple class="mt-2 so-panel">
              <v-expansion-panel>
                <v-expansion-panel-header class="so-panel__header">
                  <span class="subtitle-2 font-weight-medium">Observações</span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-row dense class="mt-1">
                    <v-col cols="12">
                      <v-textarea
                        label="Observações"
                        v-model="props.formulario.observacao"
                        outlined dense hide-details
                        rows="3"
                        auto-grow
                        :readonly="formularioBloqueado"
                      />
                    </v-col>
                  </v-row>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- SECAO: Follow-Up -->
            <v-expansion-panels v-model="panelFollowUp" flat multiple class="mt-2 so-panel">
              <v-expansion-panel>
                <v-expansion-panel-header class="so-panel__header">
                  <span class="subtitle-2 font-weight-medium">Follow-Up</span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <div class="d-flex align-center mb-2" style="gap:8px">
                    <v-btn v-if="podeIncluirFollowUp" icon small color="primary" @click="abrirNovoFollowUp()">
                      <v-icon>mdi-plus</v-icon>
                    </v-btn>
                    <v-text-field placeholder="Search" dense outlined hide-details style="max-width:220px" />
                  </div>
                  <v-data-table
                    :headers="headersFollowUp"
                    :items="props.followUps"
                    dense hide-default-footer class="elevation-0"
                    no-data-text="No data available"
                  >
                    <template v-slot:item.data="{ item }">
                      {{ formatarData(item.data) }}
                    </template>
                  </v-data-table>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            <!-- SECAO: Revisoes -->
            <v-expansion-panels v-model="panelRevisoes" flat class="mt-2 so-panel">
              <v-expansion-panel>
                <v-expansion-panel-header class="so-panel__header">
                  <span class="subtitle-2 font-weight-medium">Revisoes</span>
                </v-expansion-panel-header>
                <v-expansion-panel-content>
                  <v-data-table
                    :headers="headersRevisoes"
                    :items="props.revisoes"
                    dense hide-default-footer class="elevation-0"
                    no-data-text="No data available"
                  >
                    <template v-slot:item.dateGenerate="{ item }">
                      {{ formatarData(item.dateGenerate) }}
                    </template>
                    <template v-slot:item._mirror_tipoDeEquipamento="{ item }">
                      {{ item._mirror_tipoDeEquipamento || '-' }}
                    </template>
                  </v-data-table>
                </v-expansion-panel-content>
              </v-expansion-panel>
            </v-expansion-panels>

            </fieldset>

        </div>
      </div>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogNovoItem" max-width="900px" persistent :retain-focus="false">
      <v-card>
        <v-card-title class="headline">{{ tituloDialogItem }}</v-card-title>
        <v-card-text class="pt-4">
          <fieldset class="so-item-fieldset" :disabled="itemSomenteLeitura">
          <v-row dense>
            <v-col cols="12" md="4">
              <v-autocomplete
                label="Item"
                v-model="novoItemForm.itemId"
                :items="itensCatalogo"
                :loading="itensCatalogoCarregando"
                item-text="display"
                item-value="valueId"
                :filter="filtroItemCatalogo"
                outlined
                dense
                hide-details="auto"
                :error-messages="novoItemErros.itemId"
                @change="aoAlterarItemSelecionado"
              />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field
                label="Descrição"
                v-model="novoItemForm.descricao"
                outlined
                dense
                hide-details="auto"
                :error-messages="novoItemErros.descricao"
              />
            </v-col>
            <v-col cols="12" md="2">
              <v-text-field
                label="Qtd"
                v-model="novoItemForm.quantidade"
                type="number"
                outlined
                dense
                hide-details="auto"
                :error-messages="novoItemErros.quantidade"
                @input="recalcularNovoItemForm()"
              />
            </v-col>

            <v-col cols="12" md="3">
              <v-text-field
                label="Custo Disp. s/ICMS, PIS, COFINS c/IPI"
                :value="novoItemForm.valorDisponivel"
                outlined
                dense
                hide-details
                @input="(v) => aoDigitarMoedaNovoItem('valorDisponivel', v)"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="% PIS Custo"
                v-model="novoItemForm.porcentagemPIS"
                outlined
                dense
                hide-details
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="% COFINS Custo"
                v-model="novoItemForm.porcentagemCOFINS"
                outlined
                dense
                hide-details
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="KI"
                v-model="novoItemForm.ki"
                outlined
                dense
                hide-details
                @input="recalcularNovoItemForm()"
              />
            </v-col>

            <v-col cols="12" md="3">
              <v-text-field
                label="% ISS da Venda"
                v-model="novoItemForm.porcentagemISS"
                outlined
                dense
                hide-details
                @input="recalcularNovoItemForm()"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="% ICMS Venda"
                v-model="novoItemForm.porcentagemICMSVenda"
                outlined
                dense
                hide-details
                @input="recalcularNovoItemForm()"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="% IPI Venda"
                v-model="novoItemForm.pocentagemIPIVenda"
                outlined
                dense
                hide-details
                @input="recalcularNovoItemForm()"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="Preço de Venda c/ICMS s/IPI"
                :value="novoItemForm.precoVenda_sIPI_cICMS"
                outlined
                dense
                hide-details
                @input="(v) => aoDigitarMoedaNovoItem('precoVenda_sIPI_cICMS', v)"
              />
            </v-col>

            <v-col cols="12" md="3">
              <v-text-field
                label="Preço de Venda c/ICMS c/IPI"
                :value="novoItemReadonly.precoVenda_cIPI_cICMS"
                outlined
                dense
                hide-details
                readonly
                class="so-readonly"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="Custo Total (Qtd × Valor Disponível)"
                :value="novoItemReadonly.valorDisponivelTotal"
                outlined
                dense
                hide-details
                readonly
                class="so-readonly"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="Preço de Venda s/ICMS s/IPI"
                :value="novoItemReadonly.precoVenda_sIPI_sICMS"
                outlined
                dense
                hide-details
                readonly
                class="so-readonly"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="Preço de Venda c/ISS"
                :value="novoItemReadonly.precoVenda_cISS"
                outlined
                dense
                hide-details
                readonly
                class="so-readonly"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="KI do Custo"
                :value="novoItemReadonly.kidoCusto"
                outlined
                dense
                hide-details
                readonly
                class="so-readonly"
              />
            </v-col>

            <v-col cols="12" md="3">
              <v-text-field
                label="KV"
                :value="novoItemReadonly.kv"
                outlined
                dense
                hide-details
                readonly
                class="so-readonly"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="Valor ICMS"
                :value="novoItemReadonly.valorICMS"
                outlined
                dense
                hide-details
                readonly
                class="so-readonly"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="Valor IPI"
                :value="novoItemReadonly.valorIPI"
                outlined
                dense
                hide-details
                readonly
                class="so-readonly"
              />
            </v-col>
            <v-col cols="12" md="3">
              <v-text-field
                label="Valor Total (Qtd × Preço s/IPI)"
                :value="novoItemReadonly.vlrTotal"
                outlined
                dense
                hide-details
                readonly
                class="so-readonly"
              />
            </v-col>

          </v-row>
          </fieldset>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text type="button" @click.stop="cancelarNovoItem">{{ modoVisualizacaoItem ? 'Fechar' : 'Cancelar' }}</v-btn>
          <v-btn v-if="!modoVisualizacaoItem" color="primary" dark type="button" @click.stop="adicionarNovoItem">Adicionar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogConfirmarFechamentoCancelado" max-width="700px" persistent>
      <v-card>
        <v-card-title class="headline">Atenção</v-card-title>
        <v-card-text>
          Esta Solicitação tem como último follow up cadastrado com o status CANCELADO, ao fechar a solicitação não poderá mais ser editada.
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cancelarFechamentoCancelado">Cancelar</v-btn>
          <v-btn color="error" dark @click="confirmarFechamentoCancelado">Confirmar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialogConfirmarConclusao" max-width="700px" persistent>
      <v-card>
        <v-card-title class="headline">Atenção</v-card-title>
        <v-card-text>
          Ao Concluir uma Solicitação de Orçamento ela só poderá ser Alterada por uma Revisão!
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="cancelarConfirmacaoConclusao">Cancelar</v-btn>
          <v-btn color="success" dark @click="confirmarConclusaoSolicitacao">Concluir</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Follow-Up -->
    <v-dialog v-model="dialogFollowUp" max-width="560px" persistent>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left color="white">mdi-message-text-outline</v-icon>
          Novo Follow-Up
        </v-card-title>
        <v-card-text class="pt-5">
          <v-row dense>
            <v-col cols="12">
              <v-textarea
                label="Descricao"
                v-model="followUpForm.description"
                outlined dense rows="3" auto-grow :error-messages="followUpErros.description"
              />
            </v-col>
            <v-col cols="12" md="6" class="mt-3">
              <v-text-field
                label="Data"
                type="date"
                v-model="followUpForm.data"
                readonly outlined dense hide-details class="so-readonly"
              />
            </v-col>
            <v-col cols="12" md="6" class="mt-3">
              <v-autocomplete
                :items="props.contatosComerciaisCliente"
                item-text="nome"
                item-value="nome"
                label="Contato Comercial"
                v-model="followUpForm.contato_comercial"
                outlined dense clearable :error-messages="followUpErros.contato_comercial"
              />
            </v-col>
            <v-col v-if="!statusFormularioConcluido" cols="12" class="mt-3">
              <v-select
                :items="statusOpcoesFollowUp"
                item-text="description"
                item-value="id"
                label="Status"
                v-model="followUpForm.status"
                outlined dense clearable :error-messages="followUpErros.status"
              />
            </v-col>
            <v-col v-else cols="12" class="mt-3">
              <v-text-field
                label="Status"
                :value="descricaoStatusConcluidoFollowUp"
                readonly
                outlined
                dense
                hide-details
                class="so-readonly"
              />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer />
          <v-btn text @click="dialogFollowUp = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="props.carregando" @click="confirmarFollowUp()">
            Salvar
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Historico Item -->
    <v-dialog v-model="historicoDialog" max-width="800px">
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left color="white">mdi-history</v-icon>
          Histórico do Item
        </v-card-title>
        <v-card-text class="pt-5">
          <div v-if="historicoItemSelected" class="mb-4">
            <p class="mb-2"><strong>Descrição:</strong> {{ historicoItemSelected.descricao }}</p>
            <p class="mb-2"><strong>Quantidade:</strong> {{ historicoItemSelected.quantidade }}</p>
            <p class="mb-2"><strong>Unidade:</strong> {{ historicoItemSelected.unidade }}</p>
            <p><strong>Observação:</strong> {{ historicoItemSelected.observacao || '-' }}</p>
          </div>
          <v-divider class="my-3" />
          <div v-if="historicoLoading" class="d-flex justify-center align-center" style="min-height: 200px">
            <v-progress-circular indeterminate color="primary" />
          </div>
          <div v-else-if="historicoItems.length === 0" class="pa-3 text-center text-muted">
            <v-icon class="mr-2">mdi-information</v-icon>
            Nenhum histórico disponível
          </div>
          <v-data-table
            v-else
            :headers="[
              { text: 'Data/Hora', value: 'data', sortable: false },
              { text: 'Descrição', value: 'descricao', sortable: false },
              { text: 'Status', value: 'status', sortable: false },
              { text: 'Quantidade', value: 'quantidade', sortable: false },
              { text: 'Responsável', value: 'responsavel', sortable: false }
            ]"
            :items="historicoItems"
            dense hide-default-footer class="elevation-0"
            no-data-text="No data available"
          >
            <template v-slot:item.data="{ item }">
              {{ item.data ? new Date(item.data).toLocaleString('pt-BR') : '-' }}
            </template>
          </v-data-table>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer />
          <v-btn text @click="historicoDialog = false">Fechar</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Dialog: Novo Contato -->
    <v-dialog v-model="abrirNovoContatoModel" max-width="650px" persistent>
      <v-card>
        <v-card-title class="primary white--text">
          <v-icon left color="white">mdi-account-plus</v-icon>
          {{ tipoContatoDialog === 'tecnico' ? 'Novo contato técnico' : 'Novo contato comercial' }}
        </v-card-title>
        <v-card-text class="pt-6">
          <v-row>
            <v-col cols="12" md="6">
              <v-text-field label="Nome" v-model="props.novoContato.nome" outlined dense />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field label="E-mail" v-model="props.novoContato.email" outlined dense />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field label="Telefone" v-model="props.novoContato.telefone" outlined dense />
            </v-col>
            <v-col cols="12" md="6">
              <v-text-field label="Celular" v-model="props.novoContato.celular" outlined dense />
            </v-col>
          </v-row>
        </v-card-text>
        <v-card-actions class="px-6 pb-4">
          <v-spacer />
          <v-btn text @click="abrirNovoContatoModel = false">Cancelar</v-btn>
          <v-btn color="primary" :loading="props.carregando" @click="emits('criar-contato', { tipo: tipoContatoDialog })">
            Salvar contato
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>

  </template>

<style scoped>
.so-modal {
  background: #fafafa;
}

.so-topbar {
  display: grid;
  grid-template-columns: auto minmax(0, 1fr) auto;
  align-items: center;
  gap: 12px;
  padding: 8px 16px;
  background-color: var(--v-primary-base);
  color: white;
  position: sticky;
  top: 0;
  z-index: 10;
}

.so-topbar__left-actions {
  display: flex;
  gap: 8px;
  align-items: center;
  min-width: 0;
  flex-wrap: nowrap;
}

.so-topbar__title {
  margin: 0;
  text-align: center;
  font-size: 1.15rem;
  font-weight: 600;
  color: #ffffff;
  display: flex;
  align-items: center;
  justify-content: center;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.so-topbar__title .v-icon {
  color: #ffffff !important;
}

.so-topbar__actions {
  display: flex;
  gap: 8px;
  flex-wrap: nowrap;
  justify-content: flex-end;
  overflow-x: auto;
  overflow-y: hidden;
  min-width: 0;
  scrollbar-width: thin;
}

.so-topbar__btn {
  width: 136px;
  min-width: 136px !important;
  white-space: nowrap;
  flex: 0 0 136px;
}

.so-topbar__btn--auto-width {
  width: auto !important;
  min-width: 0 !important;
  max-width: 100%;
  white-space: nowrap !important;
  padding-left: 16px;
  padding-right: 16px;
  box-sizing: border-box;
  overflow: visible;
  text-overflow: initial;
  flex: 0 0 auto;
}

.so-topbar__btn--close {
  width: 96px;
  min-width: 96px !important;
  flex-basis: 96px;
}

.so-topbar__btn--proposal {
  width: 224px;
  min-width: 224px !important;
  flex-basis: 224px;
}

.so-body {
  height: calc(100vh - 60px);
  overflow: auto;
  overflow-x: hidden;
}

.so-form {
  padding: 20px 24px;
}

.so-fieldset {
  border: 0;
  margin: 0;
  padding: 0;
  min-width: 0;
}

.so-panel {
  border: 1px solid #e0e0e0;
  border-radius: 4px;
}

.so-panel .so-panel__header {
  background: #f5f5f5;
  min-height: 40px !important;
}

.so-readonly .v-input__slot {
  background: #f5f5f5 !important;
}

.so-readonly input,
.so-readonly textarea,
.so-form--readonly input,
.so-form--readonly textarea {
  color: rgba(0, 0, 0, 0.87) !important;
  -webkit-text-fill-color: rgba(0, 0, 0, 0.87) !important;
}

.so-readonly .v-label,
.so-form--readonly .v-label {
  color: rgba(0, 0, 0, 0.6) !important;
}

.so-campo-obrigatorio .v-input__slot {
  border-color: #ff5252 !important;
  background: #fff8f8 !important;
}

.so-campo-obrigatorio fieldset {
  border-color: #ff5252 !important;
}

.so-form--readonly .v-input {
  pointer-events: none;
}

.so-form--readonly .v-input__slot {
  background: #f5f5f5 !important;
}

.so-item-descricao {
  white-space: normal !important;
  word-break: break-word;
  line-height: 1.25;
}

.so-item-fieldset {
  border: 0;
  margin: 0;
  padding: 0;
  min-width: 0;
}

@media (max-width: 1200px) {
  .so-topbar {
    gap: 8px;
    padding: 8px 12px;
  }

  .so-topbar__title {
    font-size: 1rem;
  }

  .so-topbar__btn {
    width: 124px;
    min-width: 124px !important;
    flex-basis: 124px;
  }

  .so-topbar__btn--auto-width {
    width: auto !important;
    min-width: 0 !important;
    flex-basis: auto;
  }
}

@media (max-width: 960px) {
  .so-topbar {
    grid-template-columns: auto 1fr;
    grid-template-areas:
      "close title"
      "actions actions";
  }

  .so-topbar__btn--close {
    grid-area: close;
  }

  .so-topbar__title {
    grid-area: title;
    justify-content: flex-start;
  }

  .so-topbar__actions {
    grid-area: actions;
  }
}
</style>
